import { useApolloClient } from '@apollo/client';
import type { TagSortOrder } from '@mnd-frontend/graphql-schema';
import { Chip, Dialog } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { SPACING_1 } from '@mnd-frontend/ui/shared/tokens';
import { purgeImageUsages } from '@mnd-frontend/utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTagDeleteMutation, useTagNamesQuery } from '../../generated/hooks';

const TagsWrapper = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING_1};
`;

const DeleteTagsModal = ({
  onClose,
  onSave,
  query,
  perPage,
  page,
  sortOrder,
  pressroomId,
  tagIds,
}: {
  onClose: () => void;
  onSave: () => void;
  query: string;
  perPage: number;
  page: number;
  sortOrder: TagSortOrder;
  pressroomId: string;
  tagIds: string[];
}) => {
  const [t] = useTranslation();
  const { showSuccessToast, showFailureToast } = useToasts();

  const cache = useApolloClient().cache;
  const [deleteTags] = useTagDeleteMutation({
    onCompleted: response => {
      if (response.tagDelete?.previousImages?.length) {
        response.tagDelete.previousImages.forEach(img => purgeImageUsages(cache, img));
      }
    },
  });

  const { data, loading } = useTagNamesQuery({
    variables: {
      pressroomId,
      ids: tagIds,
    },
  });
  const tags = data?.pressroom?.tags2.hits ?? [];
  return (
    <Dialog
      description={t('category_management_delete_categories_heading')}
      open={!loading}
      confirmAction={{
        onClick: async () => {
          try {
            const result = await deleteTags({
              variables: {
                ids: tagIds,
                query,
                perPage,
                page,
                sortOrder,
                pressroomId,
              },
            });
            const error = result.data?.tagDelete?.error;
            if (!error) {
              onSave();
              showSuccessToast(t('category_management_delete_categories_success'));
              return;
            }

            showFailureToast(error?.message ?? t('common_general_error'));
          } catch (_err) {
            showFailureToast(t('common_general_error'));
          }
        },
      }}
      onClose={() => onClose()}
    >
      <TagsWrapper>
        {tags.map(tag => (
          <li key={tag.id}>
            <Chip type="readOnly">{tag.name}</Chip>
          </li>
        ))}
      </TagsWrapper>
    </Dialog>
  );
};

export default DeleteTagsModal;
